/* eslint-disable no-duplicate-imports */
import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { NavigationService } from '@treasury/core/navigation';
import type { WireIsoLabels } from '@treasury/domain/wires';
import {
    Wire,
    WireBeneficiary,
    WireCreditor,
    WireIsoService,
    WiresService,
    WireTemplate,
} from '@treasury/domain/wires';
import { TmContainer, TmFooterService } from '@treasury/presentation';
import '@treasury/presentation/components/tm-blocking-loader';
import '@treasury/presentation/components/tm-bottom-sheet';
import '@treasury/presentation/components/tm-footer';
import { TmDialogButtonPosture, TmDialogService } from '@treasury/presentation/services/dialog';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { HeaderBarService } from '../../../services/jhd-header-bar.service';
import '../../creditors/partials/creditor-data-readonly';
import '../partials/wire-template-readonly';

export const tagName = 'wire-template-container';
@customElement(tagName)
export class WireTemplateContainer extends TmContainer {
    @property({ type: Number })
    public wireTemplateId!: number;

    @property({ type: Boolean, reflect: true })
    public hideHeader = false;

    @InjectProperty()
    declare private headerService: HeaderBarService;

    @InjectProperty()
    declare private readonly footerService: TmFooterService;

    @InjectProperty()
    declare private navService: NavigationService;

    @InjectProperty()
    declare private wireIsoService: WireIsoService;

    @InjectProperty()
    declare private readonly dialogService: TmDialogService;

    @state()
    private wire = new Wire();

    @state()
    private template = new WireTemplate();

    @InjectProperty()
    declare private wiresService: WiresService;

    @state()
    private creditorSheetOpen = false;

    @state()
    private creditor?: WireBeneficiary | WireCreditor;

    @state()
    private wireIsoLabels: WireIsoLabels = WireIsoService.legacyLabels;

    protected render() {
        return html`${this.renderBlockingLoader()}
            <div class="bg-primary overflow-y-auto">${this.renderDetails()}</div>
            ${this.renderBeneficiarySheet()}`;
    }

    protected async firstUpdated() {
        const { params } = await this.navService.getRouteData<{ id: string }>();
        this.wireTemplateId = +params.id;
        this.loading = true;
        this.wireIsoLabels = await this.wireIsoService.getLabels();

        try {
            this.template = await this.wiresService.getTemplate(this.wireTemplateId);
            if (this.template.beneficiary) {
                this.creditor = new WireCreditor(this.template.beneficiary);
            }
            this.configureHeader();
            this.configureFooter();
        } finally {
            this.loading = false;
        }
    }

    private configureHeader() {
        if (this.hideHeader) {
            return;
        }
        this.headerService.configure({
            title: 'Wire Template Details',
            backAction: () => {
                this.navService.navigate(`/wires/templates/view`);
            },
        });
    }

    private configureFooter() {
        const canEdit = this.template.permissions.some(
            (p: { permission: string }) => p.permission === 'Edit'
        );

        const canDelete = this.template.permissions.some(
            (p: { permission: string }) => p.permission === 'Delete'
        );

        this.footerService.configure({
            direction: this.isDesktop ? 'row' : 'column',
            buttonConfig: [
                {
                    text: 'Initiate wire from template',
                    importance: 'secondary',
                    onClick: () => {
                        this.navService.navigate(
                            `/wires/template/initiate/${this.template.id}/usd`
                        );
                    },
                },
                {
                    text: 'Edit template',
                    importance: 'secondary',
                    classes: canEdit ? '' : 'hidden',
                    onClick: () => {
                        this.navService.navigate(`/wires/template/edit/${this.template.id}/usd`);
                    },
                },
                {
                    text: 'Delete template',
                    error: true,
                    importance: 'primary',
                    classes: canDelete ? '' : 'hidden',
                    onClick: () => {
                        this.promptForDeletion(this.template);
                    },
                },
            ],
        });
    }

    private renderBlockingLoader() {
        if (!this.loading) {
            return nothing;
        }
        return html`<tm-blocking-loader></tm-blocking-loader>`;
    }

    private renderBeneficiarySheet() {
        return html`<tm-bottom-sheet
            .open=${this.creditorSheetOpen}
            @close=${() => {
                this.creditorSheetOpen = false;
            }}
        >
            <p slot="header-center">${this.wireIsoLabels.creditor} Detail</p>
            <tm-body .padding=${false}>
                <creditor-data-readonly .creditor=${this.creditor}></creditor-data-readonly>
            </tm-body>
        </tm-bottom-sheet>`;
    }

    private renderDetails() {
        if (this.loading) {
            return nothing;
        }

        return html`<wire-template-readonly
            .wireTemplate=${this.template}
            @view-creditor-detail=${() => {
                this.creditorSheetOpen = true;
            }}
        ></wire-template-readonly>`;
    }

    private async deleteTemplate(template: WireTemplate) {
        await this.tryFetch(
            () => this.wiresService.wiresDeleteTemplate(template.id),
            () => {
                this.navService.navigate('/wires/templates/view');
                this.notificationService.renderSuccess('Template deleted successfully');
            }
        );
    }

    private async promptForDeletion(template: WireTemplate) {
        const { reason } = await this.dialogService.open(
            `Are you sure you want to delete this template?`,
            'Delete Wire Template',
            {
                buttons: {
                    [TmDialogButtonPosture.Secondary]: {
                        label: 'Cancel',
                        reason: DynamicComponentExitReason.Cancel,
                    },
                    [TmDialogButtonPosture.Primary]: {
                        label: 'Delete',
                        reason: DynamicComponentExitReason.Confirm,
                    },
                },
                classes: ['max-w-[500px]'],
            }
        );
        if (reason === DynamicComponentExitReason.Confirm) {
            this.deleteTemplate(template);
        }
        if (reason === DynamicComponentExitReason.Cancel) {
            return;
        }
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: WireTemplateContainer;
    }
}
